import { captureException } from '@sentry/react'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { RowClassParams, RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IUserAdmin } from '~/types/user'

import columns from './columns'

import ManageUserModal from '../ManageUserModal'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useTouchDown } from '~/hooks/useTouchDown'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useMwmUsersListQuery } from '~/queries/user-management'
import { dateSort } from '~/utils/date'
import { displayToast } from '~/utils/toast'

const UserListing = (): ReactElement => {
  /** Local state */

  const authUsersWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.AUTH.USERS.WRITE])

  const navigate = useNavigate()

  const gridRef = useRef<AgGridReact<IUserAdmin>>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [isModalOpen, setModalOpen] = useState(false)

  const { isTouchDown, setIsTouchDown } = useTouchDown('Meta')

  /** Queries & Mutations */

  const { data: users } = useMwmUsersListQuery()

  /** Effects */

  useDocumentTitle('IAM Users')

  /** Handlers */

  const handleOpenModal = (isOpen: boolean) => () => setModalOpen(isOpen)

  const handleSuccess = () => {
    setModalOpen(false)
    displayToast('User successfully created', 'success')
  }

  const handleFailure = (error: unknown) => {
    captureException(error)
    displayToast('User could not be created', 'error')
    setModalOpen(false)
  }

  const handleRowClicked = (evt: RowClickedEvent) => {
    if (isTouchDown) {
      window.open(`/mwm-users/users/${evt.data.user_id}`, '_blank', 'rel=noopener noreferrer')
      setIsTouchDown(false)
    } else {
      navigate(`/mwm-users/users/${evt.data.user_id}`)
    }
  }

  /** Render */

  const sortUsers = users?.data?.sort(({ updated_at_ts: aUpdatedDate }, { updated_at_ts: bUpdatedDate }) =>
    dateSort(aUpdatedDate, bUpdatedDate, 'desc')
  )

  return (
    <>
      <Layout className="users-view" withPadding>
        <ViewTitle className="layout-view-title">IAM Users</ViewTitle>
        <ContentLayout>
          <TableWrapper
            addButton
            addButtonDisabled={!authUsersWriteAuthorized}
            addButtonLabel="New user"
            addButtonTooltip={!authUsersWriteAuthorized ? 'unauthorized' : undefined}
            className="users-table with-min-height"
            columnDefs={columns}
            entriesName="users"
            getRowStyle={({ data }: RowClassParams<IUserAdmin>) =>
              data?.status === 'blocked' ? { opacity: 0.5 } : undefined
            }
            onAddButtonClick={handleOpenModal(true)}
            onRowClicked={handleRowClicked}
            ref={gridRef}
            rowData={sortUsers}
            tableRef={tableRef}
            withPagination={false}
            withSearchBar
          />
        </ContentLayout>
      </Layout>
      {authUsersWriteAuthorized ? (
        <ManageUserModal
          isOpen={isModalOpen}
          onClose={handleOpenModal(false)}
          onFailure={handleFailure}
          onSuccess={handleSuccess}
        />
      ) : null}
    </>
  )
}

export default UserListing
